// src/routes/AllRoutes.tsx
import React, { Suspense } from 'react';
import { Navigate, useRoutes } from 'react-router-dom';
import { DefaultLayout, VerticalLayout, HorizontalLayout, DetachedLayout, FullLayout } from 'layouts';
import PrivateRoute from './PrivateRoute';
import PrivateRouteTipo2 from './PrivateRouteTipo2';
import Root from './Root';
import { LayoutTypes } from 'appConstants';
import { useRedux } from 'hooks';
import UploadOnlyLayout from 'layouts/UploadOnlyLayout';

// Auth 
const Login = React.lazy(() => import('pages/account/Login'));
const Logout = React.lazy(() => import('pages/account/Logout'));
const Register = React.lazy(() => import('pages/account/Register'));
const Confirm = React.lazy(() => import('pages/account/Confirm'));
const ForgetPassword = React.lazy(() => import('pages/account/ForgetPassword'));
const LockScreen = React.lazy(() => import('pages/account/LockScreen'));

// Dashboard
const AnalyticsDashboard = React.lazy(() => import('pages/dashboard/Analytics'));

// Home
const Home = React.lazy(() => import('pages/home/Home'));

// Account Activation
const AccountActivation = React.lazy(() => import('pages/accountActivation/AccountActivation'));
const ActivationLoading = React.lazy(() => import('pages/accountActivation/Components/ActivationLoading'));
const ActivationError = React.lazy(() => import('pages/accountActivation/Components/ActivationError'));
const AlreadyActivated = React.lazy(() => import('pages/accountActivation/Components/AlreadyActivated'));
const ActivationSuccess = React.lazy(() => import('pages/accountActivation/Components/ActivationSuccess'));
const CreatePassword = React.lazy(() => import('pages/accountActivation/Components/CreatePassword'));

// DocumentUploadWizard
const DocumentUploadWizard = React.lazy(() => import('pages/DocumentUploadWizard/DocumentUploadWizard'));

// TaxClaimant
const TaxClaimant = React.lazy(() => import('pages/TaxClaimant/TaxClaimant'));

// Triage
const Triage = React.lazy(() => import('pages/Triage/Triage'));

// Docs
const DocumentManager = React.lazy(() => import('pages/DocumentManager/DocumentManager'));

// Erros e manutenção
const ErrorPageNotFound = React.lazy(() => import('pages/error/PageNotFound'));
const ServerError = React.lazy(() => import('pages/error/ServerError'));
const Maintenance = React.lazy(() => import('pages/other/Maintenance'));

const loading = () => <div className="vh-100 d-flex align-items-center justify-content-center">Carregando…</div>;

const LoadComponent = ({ component: Component }: { component: React.LazyExoticComponent<React.ComponentType<any>> }) => (
  <Suspense fallback={loading()}>
    <Component />
  </Suspense>
);

const AllRoutes = () => {
  const { appSelector } = useRedux();
  const { layout } = appSelector(state => ({ layout: state.Layout }));
  
  const getLayout = () => {
    switch (layout.layoutType) {
      case LayoutTypes.LAYOUT_HORIZONTAL: return HorizontalLayout;
      case LayoutTypes.LAYOUT_DETACHED:   return DetachedLayout;
      case LayoutTypes.LAYOUT_FULL:       return FullLayout;
      default:                            return VerticalLayout;
    }
  };
  const Layout = getLayout();

  return useRoutes([
    // root redireciona para dashboard
    { path: '/', element: <Root /> },

    // rotas públicas
    {
      path: '/',
      element: <DefaultLayout />,
      children: [
        { index: true, element: <LoadComponent component={Home} /> },
        {
          path: 'account',
          children: [
            { path: 'login', element: <LoadComponent component={Login} /> },
            { path: 'register', element: <LoadComponent component={Register} /> },
            { path: 'confirm', element: <LoadComponent component={Confirm} /> },
            { path: 'forget-password', element: <LoadComponent component={ForgetPassword} /> },
            { path: 'lock-screen', element: <LoadComponent component={LockScreen} /> },
            { path: 'logout', element: <LoadComponent component={Logout} /> },
          ],
        },
        { path: 'error-404', element: <LoadComponent component={ErrorPageNotFound} /> },
        { path: 'error-500', element: <LoadComponent component={ServerError} /> },
        { path: 'maintenance', element: <LoadComponent component={Maintenance} /> },
        { path: 'accountactivation', element: <LoadComponent component={AccountActivation} /> },
        { path: 'activationloading', element: <LoadComponent component={ActivationLoading} /> },
        { path: 'activationerror', element: <LoadComponent component={ActivationError} /> },
        { path: 'alreadyactivated', element: <LoadComponent component={AlreadyActivated} /> },
        { path: 'activationsuccess', element: <LoadComponent component={ActivationSuccess} /> },
        { path: 'createpassword', element: <LoadComponent component={CreatePassword} /> },
      ],
    },

    // rotas protegidas para usuário tipo 1
    {
      path: '/',
      element: <PrivateRoute component={Layout} />,
      children: [
        {
          path: 'dashboard',
          children: [
            { path: 'analytics', element: <LoadComponent component={AnalyticsDashboard} /> },
          ],
        },
        { path: 'taxclaimant', element: <LoadComponent component={TaxClaimant} /> },
        { path: 'documentmanager', element: <LoadComponent component={DocumentManager} /> },
        { path: 'triage', element: <LoadComponent component={Triage} /> },
      ],
    },

    // rota protegida para usuário tipo 2
    {
      path: '/',
      element: <PrivateRouteTipo2 component={UploadOnlyLayout} />,
      children: [
        { path: 'txclmt/upload', element: <LoadComponent component={DocumentUploadWizard} /> },
      ],
    },

    // qualquer outra cai no 404
    { path: '*', element: <Navigate to="/error-404" replace /> },
  ]);
};

export { AllRoutes };
